import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Card, IconButton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../theme/theme';

export interface PageSectionProps {
  children?: React.ReactNode;
  adornment?: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  icon?: React.ElementType;
  onExpandedChange?: (expanded: boolean) => void;
  subtitle?: string;
  title?: string;
}

const PageSectionContainer = styled(Card)`
  margin-top: ${theme.spacing(2)};
  padding: ${theme.spacing(2)};
  background-color: ${theme.palette.background.paper};
`;

const PageSectionHeader = styled(Box)`
  margin-bottom: ${theme.spacing(3)};
`;

export function PageSection(props: PageSectionProps) {
  const {
    adornment,
    icon,
    title,
    subtitle,
    children,
    collapsible = false,
    defaultExpanded = true,
    onExpandedChange,
  } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  return (
    <PageSectionContainer className="page-section">
      {(title || subtitle) && (
        <PageSectionHeader className="page-section-header">
          {title && (
            <>
              <Box
                className="page-section-title-container"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={subtitle ? 1 : 0}
              >
                <Box display="flex" alignItems="center">
                  {icon && (
                    <Box className="page-icon" sx={{ mr: 1 }}>
                      {React.createElement(icon)}
                    </Box>
                  )}
                  <Typography className="page-section-title" variant="h4">
                    {title}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {adornment}
                  {collapsible && (
                    <IconButton
                      onClick={handleToggle}
                      size="small"
                      aria-label={expanded ? 'Collapse section' : 'Expand section'}
                      sx={{
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  width: 100,
                  height: '4px',
                  background: 'linear-gradient(135deg, #c05a3a, #d4825e)',
                  mb: 2,
                  borderRadius: '2px',
                }}
              />
            </>
          )}
          {subtitle && (
            <Typography
              className="page-section-subtitle"
              variant="subtitle1"
              sx={{ color: theme.palette.text.secondary }}
            >
              {subtitle}
            </Typography>
          )}
        </PageSectionHeader>
      )}
      {expanded && children}
    </PageSectionContainer>
  );
}
